import {
	IDrawerDataListColumn,
	PaginatedDrawerAutoDataList,
	laravelServicePaginatedListFetchConfig,
	useHasPermission,
	useLaravelCrudEntity,
} from "@dgs/core";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { PermissionName } from "../../../shared/types/permission";
import { costCategoryService } from "../shared/api/costCategoryService";
import { eventService } from "../shared/api/event/eventService";
import { eventTypeService } from "../shared/api/eventTypeService";
import { CostCategoryIndexResource } from "../shared/types/costCategory";
import { CostCategoryCreateDrawer, CostCategoryUpdateDrawer } from "./CostCategoryDrawer";
import { CostCategoryListContextMenu } from "./CostCategoryListContextMenu";

export const CostCategoryList: FC = () => {
	const { t } = useTranslation();
	const canCreate = useHasPermission<PermissionName>(["costCategory.create"]);
	const canUpdate = useHasPermission<PermissionName>(["costCategory.update"]);

	const crudOperations = useLaravelCrudEntity(
		costCategoryService,
		{
			entityName: t("Cost category"),
			messages: {
				create: t("Cost category was saved."),
				update: t("Cost category was saved."),
				delete: t("Cost category was deleted."),
			},
		},
		[],
		[eventTypeService.indexKey, eventService.indexKey, costCategoryService.indexKey],
	);

	const dataListColumns: IDrawerDataListColumn<CostCategoryIndexResource>[] = useMemo(
		() => [
			{
				heading: t("Name"),
				valueKey: "name",
				type: "value",
			},
			{
				heading: "",
				type: "actions",
				size: "max-content",
				action: (costCategory, openDrawer) => (
					<CostCategoryListContextMenu
						costCategoryId={costCategory.id}
						onEdit={openDrawer}
						onDelete={() => crudOperations.deleteEntity(costCategory.id)}
					/>
				),
			},
		],
		[crudOperations, t],
	);

	return (
		<PaginatedDrawerAutoDataList
			canCreate={canCreate}
			canUpdate={canUpdate}
			columns={dataListColumns}
			listFetchConfig={laravelServicePaginatedListFetchConfig(costCategoryService)}
			renderCreateDrawer={(drawerProps) => (
				<CostCategoryCreateDrawer
					{...drawerProps}
					onSubmit={(entityFormState) => crudOperations.createEntity(entityFormState)}
				/>
			)}
			renderUpdateDrawer={(drawerProps) => (
				<CostCategoryUpdateDrawer
					{...drawerProps}
					onSubmit={(entityFormState) => crudOperations.updateEntity(drawerProps.id, entityFormState)}
				/>
			)}
			translations={{
				title: t("Cost categories"),
				empty: t("No cost categories available."),
			}}
			initialSearch={{ search: "", searchFields: [{ label: t("Name"), name: "cost_category_translations.name" }] }}
		/>
	);
};
